import React, { useState, useEffect } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAuth } from "@shadowhealth/oidc-login";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";
import {
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

const formatPreset = (preset) => {
  const overrides = {
    convoengine_plus: "ConvoEngine Plus",
    cws: "CWS",
    semantic_only: "Semantic Only",
    lexical_vector_only: "Lexical Vector Only",
  };
  return (
    overrides[preset?.toLowerCase()] || (preset || "").replaceAll("_", " ").replaceAll(/\b\w/g, (c) => c.toUpperCase())
  );
};

// Tag with strictly highest F1 for this preset; null if tie or no data
const bestTag = (batchesByPreset, preset, tags) => {
  let best = null;
  let bestF1 = -1;
  let tie = false;
  tags.forEach((tag) => {
    const f1 = batchesByPreset[preset]?.[tag]?.f1 ?? -1;
    if (f1 > bestF1) {
      bestF1 = f1;
      best = tag;
      tie = false;
    } else if (f1 === bestF1 && f1 >= 0) {
      tie = true;
    }
  });
  return tie ? null : best;
};

const MetricBar = ({ value, color }) => (
  <Tooltip title={pct(value)}>
    <LinearProgress
      variant="determinate"
      value={(value ?? 0) * 100}
      color={color || "primary"}
      sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover" }}
    />
  </Tooltip>
);

const BatchCell = ({ batch, isBest }) => (
  <Stack spacing={1} sx={{ py: 0.5 }}>
    {/* F1 — the headline number */}
    <Stack direction="row" alignItems="baseline" spacing={0.5}>
      <Typography variant="h6" fontWeight={700} color={isBest ? "success.main" : "text.primary"}>
        {pct(batch.f1)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        F1{isBest ? " ✓" : ""}
      </Typography>
    </Stack>

    {/* Visual bar for F1 */}
    <MetricBar value={batch.f1} color={isBest ? "success" : "primary"} />

    {/* Secondary metrics — compact */}
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Tooltip title="Precision">
        <Typography variant="caption" color="text.secondary">
          P&nbsp;{pct(batch.precision)}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.disabled">
        ·
      </Typography>
      <Tooltip title="Recall">
        <Typography variant="caption" color="text.secondary">
          R&nbsp;{pct(batch.recall)}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.disabled">
        ·
      </Typography>
      <Tooltip title="Top-1 hit rate">
        <Typography variant="caption" color="text.secondary">
          Top-1&nbsp;{pct(batch.top_answer_rate)}
        </Typography>
      </Tooltip>
    </Stack>

    <Typography variant="caption" color="text.disabled">
      {batch.total_questions} questions · {new Date(batch.created_at).toLocaleDateString()}
    </Typography>
  </Stack>
);

const EmptyCell = () => (
  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
    Not run
  </Typography>
);

const EvaluationComparePage = () => {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const tags = searchParams.getAll("tags");
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tags.length) return;
    setLoading(true);
    callApi(
      `/eval/tags/compare?${tags.map((t) => "tags=" + encodeURIComponent(t)).join("&")}`,
      "GET",
      undefined,
      auth.user?.access_token,
    )
      .then((resp) => setBatches(resp.batches || []))
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => setLoading(false));
  }, [searchParams.toString()]); // noqa

  // Group: batchesByPreset[preset][tag] = latest batch
  const batchesByPreset = {};
  const presets = [];
  batches.forEach((b) => {
    if (!batchesByPreset[b.preset]) {
      batchesByPreset[b.preset] = {};
      presets.push(b.preset);
    }
    const existing = batchesByPreset[b.preset][b.tag];
    if (!existing || new Date(b.created_at) > new Date(existing.created_at)) {
      batchesByPreset[b.preset][b.tag] = b;
    }
  });

  // Deduplicate presets (push can add duplicates if tags share presets)
  const uniquePresets = [...new Set(presets)];

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Breadcrumbs>
              <MuiLink component={RouterLink} underline="hover" color="inherit" to="/evaluations">
                Evaluations
              </MuiLink>
              <Typography color="text.primary">Compare</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h5" component="h1">
                Comparing
              </Typography>
              {tags.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  variant="outlined"
                  color="primary"
                  component={RouterLink}
                  to={`/evaluations/${encodeURIComponent(t)}`}
                  clickable
                />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Highest F1 per row marked in green. Not run = no batch for that tag + preset combination.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress />}

      {!loading && uniquePresets.length === 0 && (
        <Card>
          <CardContent>
            <Typography color="text.secondary">No eval data found for the selected tags.</Typography>
          </CardContent>
        </Card>
      )}

      {uniquePresets.length > 0 && (
        <Card>
          <CardContent sx={{ overflowX: "auto", p: 0, "&:last-child": { pb: 0 } }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 700, width: 180, pl: 3 }}>Preset</TableCell>
                  {tags.map((tag) => (
                    <TableCell key={tag} align="left" sx={{ fontWeight: 700, minWidth: 200 }}>
                      <MuiLink
                        component={RouterLink}
                        to={`/evaluations/${encodeURIComponent(tag)}`}
                        underline="hover"
                        color="inherit"
                      >
                        {tag}
                      </MuiLink>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {uniquePresets.map((preset, i) => {
                  const winner = bestTag(batchesByPreset, preset, tags);
                  return (
                    <TableRow
                      key={preset}
                      sx={{ "&:last-child td": { border: 0 }, bgcolor: i % 2 === 0 ? "transparent" : "action.hover" }}
                    >
                      <TableCell sx={{ verticalAlign: "top", pl: 3, pt: 2 }}>
                        <Tooltip title={preset} placement="right">
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {formatPreset(preset)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {tags.map((tag) => {
                        const batch = batchesByPreset[preset]?.[tag];
                        const isBest = winner === tag;
                        return (
                          <TableCell
                            key={tag}
                            sx={{
                              verticalAlign: "top",
                              pt: 2,
                              bgcolor: isBest ? "success.50" : undefined,
                              borderLeft: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            {batch ? <BatchCell batch={batch} isBest={isBest} /> : <EmptyCell />}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default EvaluationComparePage;
